import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from '../config';
import { persistentStorage } from '../utils/storage';
import { pushNotificationManager } from '../utils/pushNotifications';
import { useSnackbar } from './SnackbarContext';

const useStyles = makeStyles(() => ({
    rootContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'white',
        overflow: 'hidden',
        borderRadius: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Nunito, Poppins, Open Sans, Inter, sans-serif',
        padding: '20px',
        boxSizing: 'border-box',
    },
    successIcon: {
        width: 62,
        height: 62,
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 40,
        position: 'relative',
    },
    checkmark: {
        width: 29.13,
        height: 22.60,
        background: 'white',
        mask: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'3\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'20,6 9,17 4,12\'/%3E%3C/svg%3E") no-repeat center',
        maskSize: 'contain',
    },
    title: {
        color: '#C084FC',
        fontSize: 18.32,
        fontFamily: 'Poppins',
        fontWeight: '600',
        lineHeight: '24px',
        wordWrap: 'break-word',
        textAlign: 'center',
        marginBottom: 12,
        maxWidth: 320,
    },
    message: {
        width: '100%',
        maxWidth: 320,
        textAlign: 'center',
        color: '#B6B6B6',
        fontSize: 16,
        fontFamily: 'Poppins',
        fontWeight: '500',
        lineHeight: '22px',
        wordWrap: 'break-word',
        marginBottom: 30,
    },
    orgName: {
        color: '#724F96',
        fontWeight: '600',
    },
    refreshButton: {
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        color: 'white',
        borderRadius: 10,
        padding: '12px 24px',
        fontSize: 16,
        fontFamily: 'Poppins',
        fontWeight: '600',
        textTransform: 'none',
        boxShadow: '0px 4px 12px rgba(192, 132, 252, 0.3)',
        '&:hover': {
            background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#9E9E9E',
        },
    },
    logoutButton: {
        marginTop: 16,
        color: '#724F96',
        borderRadius: 10,
        padding: '10px 24px',
        fontSize: 15,
        fontFamily: 'Poppins',
        fontWeight: '600',
        textTransform: 'none',
        border: '1px solid #C084FC',
        background: 'white',
        '&:hover': {
            background: 'rgba(192, 132, 252, 0.08)',
        },
    },
}));

const ROLE_LABEL = {
    valet: 'Valet',
    supervisor: 'Supervisor',
    serviceboy: 'Service Boy',
};

const AwaitingAssignment = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [userId, setUserId] = useState(null);
    const [userName, setUserName] = useState('');
    const [role, setRole] = useState('');
    const [orgName, setOrgName] = useState('');
    const [isChecking, setIsChecking] = useState(false);

    useEffect(() => {
        (async () => {
            const uid = await persistentStorage.get('currentUserId');
            const name = await persistentStorage.get('currentUserName');
            const r = await persistentStorage.get('currentRole');
            const vspName = await persistentStorage.get('currentValetServiceName');
            setUserId(uid);
            setUserName(name || '');
            setRole(r || '');
            setOrgName(vspName || '');
        })();
    }, []);

    const handleLogout = async () => {
        try {
            if (userId) {
                await axios.patch(`${config.API_BASE_URL}/user-status/logout`, {
                    user_id: userId,
                });
            }
        } catch (err) {
            console.error('Logout API error:', err);
        } finally {
            // Unregister FCM BEFORE clearing storage — we need currentUserId
            // in storage to identify the user↔device row to delete.
            try { await pushNotificationManager.unregisterForUser(); } catch (_) { /* ignore */ }
            try { await persistentStorage.clear?.(); } catch (_) { /* ignore */ }
            try { localStorage.clear(); } catch (_) { /* native build */ }
            navigate('/associates-login', { replace: true });
        }
    };

    const handleCheckAgain = async () => {
        if (!userId) return;
        setIsChecking(true);
        try {
            const resp = await axios.get(`${config.API_BASE_URL}/users/${userId}/workspaces`);
            const all = resp.data?.hotels || [];
            // Real workspaces are those with a hotel or an event. The synthetic
            // org-workspace from _build_hotels_payload Branch 5-fallback carries
            // is_org_workspace=true and hotel_id=null/event_id=null.
            const real = all.filter((h) => h.hotel_id || h.event_id);

            if (real.length === 0) {
                showSnackbar(
                    'No property or event assigned yet. Please contact your organisation.',
                    'info'
                );
                return;
            }

            if (real.length > 1) {
                // Reuse the standard picker. HotelSelection reads first_name +
                // designation off userData, neither of which is in the stored
                // currentUser blob — fetch the full user row so the picker
                // doesn't write the literal string "undefined" into storage.
                let userData = {};
                try {
                    const u = await axios.get(`${config.API_BASE_URL}/users/${userId}`);
                    userData = u.data || {};
                } catch (_) {
                    const storedUser = await persistentStorage.get('currentUser');
                    try { userData = storedUser ? JSON.parse(storedUser) : {}; } catch (__) { userData = {}; }
                }
                navigate('/hotel-selection', { state: { userData, hotels: real } });
                return;
            }

            // Exactly one real workspace: persist its hotel/event and route by role.
            const w = real[0];
            await persistentStorage.set('hotel_id', w.hotel_id);
            await persistentStorage.set('currentEventId', w.event_id || null);
            if (w.hotel_id) localStorage.setItem('hotel_id', w.hotel_id);
            else localStorage.removeItem('hotel_id');
            if (w.event_id) localStorage.setItem('currentEventId', w.event_id);
            else localStorage.removeItem('currentEventId');

            // Keep currentUser in sync so downstream screens read consistent IDs.
            const storedUser = await persistentStorage.get('currentUser');
            if (storedUser) {
                try {
                    const parsed = JSON.parse(storedUser);
                    const updated = { ...parsed, hotel_id: w.hotel_id, event_id: w.event_id || null };
                    await persistentStorage.set('currentUser', JSON.stringify(updated));
                    localStorage.setItem('currentUser', JSON.stringify(updated));
                } catch (_) { /* ignore */ }
            }

            const targetRole = w.role || role;
            if (targetRole === 'supervisor') navigate('/supervisor-dashboard');
            else if (targetRole === 'valet' || targetRole === 'serviceboy') navigate('/valet-dashboard');
            else navigate('/dashboard');
        } catch (err) {
            console.error('Check assignment error:', err);
            showSnackbar('Could not check assignment status. Please try again.', 'error');
        } finally {
            setIsChecking(false);
        }
    };

    const roleLabel = ROLE_LABEL[role] || '';
    const greeting = userName ? `Hi ${userName.split(' ')[0]},` : 'Hi,';

    return (
        <div className={classes.rootContainer}>
            <div className={classes.successIcon}>
                <div className={classes.checkmark} />
            </div>

            <div className={classes.title}>
                {greeting} you are verified.
            </div>

            <div className={classes.message}>
                {roleLabel ? `Your account (${roleLabel}) ` : 'Your account '}
                {orgName ? (<>belongs to <span className={classes.orgName}>{orgName}</span>.</>) : 'is ready.'}
                <br />
                You haven't been assigned to a property or event yet.
                <br />
                Tap Check Again once your organisation confirms your assignment.
            </div>

            <Button
                className={classes.refreshButton}
                onClick={handleCheckAgain}
                disabled={isChecking || !userId}
                startIcon={isChecking ? <CircularProgress size={16} color="inherit" /> : null}
            >
                {isChecking ? 'Checking...' : 'Check Again'}
            </Button>

            <Button
                className={classes.logoutButton}
                onClick={handleLogout}
            >
                Logout
            </Button>
        </div>
    );
};

export default AwaitingAssignment;
